import { useCallback, useMemo, useState } from 'react';
import { ApolloError } from '@apollo/client';
import {
  GetRecordGrantsDocument,
  GetRecordGrantsQuery,
  RecordGrantLevel,
  useGetRecordGrantsQuery,
  useSetRecordGrantsMutation,
} from '@/lib/graphql/types-and-hooks';
import { useWorkspaceId } from '@/lib/hooks/use-workspace-id';
import { isLovablePreview } from '@/lib/utils/environment';

export type GrantLevel = 'view' | 'edit';

export interface RecordGrant {
  groupId: string;
  level: GrantLevel;
}

/** Person-level grant (grid Privacy popover's "Share with People"). */
export interface RecordUserGrant {
  userId: string;
  level: GrantLevel;
}

export interface RecordGrantsState {
  grants: RecordGrant[];
  userGrants: RecordUserGrant[];
  /** 'default' = managed by the workspace's per-entity default; 'custom' = set via the share dialog. */
  source: 'default' | 'custom';
  /** The per-entity default that would apply on reset; null for entities without one. */
  workspaceDefault: string | null;
}

interface MutationResult {
  success: boolean;
  error?: string;
}

// Preview mode: module-level store so a dialog round-trips its own saves.
const previewGrants = new Map<string, RecordGrantsState>();

function toState(
  recordGrants: NonNullable<GetRecordGrantsQuery['recordGrants']> | null | undefined
): RecordGrantsState {
  const view = (recordGrants?.viewGroupIds || []).map(String);
  const edit = new Set((recordGrants?.editGroupIds || []).map(String));
  const viewUsers = (recordGrants?.viewUserIds || []).map(String);
  const editUsers = new Set((recordGrants?.editUserIds || []).map(String));
  return {
    grants: view.map((groupId) => ({ groupId, level: edit.has(groupId) ? 'edit' : 'view' })),
    userGrants: viewUsers.map((userId) => ({
      userId,
      level: editUsers.has(userId) ? 'edit' : 'view',
    })),
    source: recordGrants?.source === 'custom' ? 'custom' : 'default',
    workspaceDefault: recordGrants?.workspaceDefault ?? null,
  };
}

/**
 * A record's group grants (share dialog state). Replace-all semantics: saving
 * writes the dialog's whole state; empty grants = deliberately visible to the
 * whole workspace (still "custom"); resetToDefault returns the record to
 * workspace-default management.
 *
 * RecordGrants has no `id`, so it is NOT normalized in the Apollo cache — the
 * mutation writes the result back via cache.writeQuery (house rule: no
 * refetchQueries on update mutations).
 */
export function useRecordGrants(recordId: string | null, opts?: { skip?: boolean }) {
  const workspaceId = useWorkspaceId();
  const preview = isLovablePreview();
  const [previewVersion, setPreviewVersion] = useState(0);

  const { data, loading, error } = useGetRecordGrantsQuery({
    variables: { workspaceId: workspaceId || '', recordId: recordId || '' },
    skip: !!opts?.skip || preview || !workspaceId || !recordId,
    fetchPolicy: 'cache-and-network',
  });

  const [setGrantsMutation] = useSetRecordGrantsMutation();

  const state = useMemo<RecordGrantsState>(() => {
    if (preview) {
      // previewVersion invalidates the memo after a preview save.
      void previewVersion;
      return (
        (recordId && previewGrants.get(recordId)) || {
          grants: [],
          userGrants: [],
          source: 'default',
          workspaceDefault: 'everyone',
        }
      );
    }
    return toState(data?.recordGrants);
  }, [preview, previewVersion, recordId, data]);

  const save = useCallback(
    async (
      grants: RecordGrant[],
      userGrants: RecordUserGrant[],
      reset: boolean
    ): Promise<MutationResult> => {
      if (preview) {
        if (recordId) {
          if (reset) {
            previewGrants.delete(recordId);
          } else {
            previewGrants.set(recordId, {
              grants,
              userGrants,
              source: 'custom',
              workspaceDefault: 'everyone',
            });
          }
          setPreviewVersion((v) => v + 1);
        }
        return { success: true };
      }
      if (!workspaceId || !recordId) return { success: false, error: 'No record selected' };
      try {
        await setGrantsMutation({
          variables: {
            workspaceId,
            recordId,
            grants: grants.map((grant) => ({
              groupId: grant.groupId,
              level: grant.level === 'edit' ? RecordGrantLevel.Edit : RecordGrantLevel.View,
            })),
            userGrants: userGrants.map((grant) => ({
              userId: grant.userId,
              level: grant.level === 'edit' ? RecordGrantLevel.Edit : RecordGrantLevel.View,
            })),
            reset,
          },
          update(cache, { data: payload }) {
            const updated = payload?.setRecordGrants?.recordGrants;
            if (!updated) return;
            cache.writeQuery<GetRecordGrantsQuery>({
              query: GetRecordGrantsDocument,
              variables: { workspaceId, recordId },
              data: { recordGrants: { __typename: 'RecordGrants', ...updated } },
            });
          },
        });
        return { success: true };
      } catch (e) {
        return { success: false, error: e instanceof Error ? e.message : 'Failed to update access' };
      }
    },
    [preview, workspaceId, recordId, setGrantsMutation]
  );

  // Replace-all on BOTH grant kinds: a groups-only caller (the share dialog has
  // no person UI) must carry the current person grants forward or its save
  // would silently wipe a popover share — hence the default to state.userGrants.
  const saveGrants = useCallback(
    (grants: RecordGrant[], userGrants?: RecordUserGrant[]) =>
      save(grants, userGrants ?? state.userGrants, false),
    [save, state.userGrants]
  );

  const resetToDefault = useCallback(() => save([], [], true), [save]);

  return {
    ...state,
    loading: preview ? false : loading,
    error: preview ? null : ((error as ApolloError | undefined) ?? null),
    saveGrants,
    resetToDefault,
  };
}
